import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@scio3/ui';
import { SHARE_LINK_ACCESS_TYPES, SHARE_LINK_ACCESS_ROLES } from '@scio3/shared-types';
import type { ShareLink } from '@scio3/shared-types';

type Props = {
  shareLink: ShareLink;
  onUpdate: (_id: string, payload: { accessType?: string; accessRole?: string }) => Promise<void>;
};

const ACCESS_TYPE_OPTIONS = [
  { value: SHARE_LINK_ACCESS_TYPES.RESTRICTED, label: 'Restricted' },
  { value: SHARE_LINK_ACCESS_TYPES.ACCOUNT, label: 'Your Account Members' },
  { value: SHARE_LINK_ACCESS_TYPES.ANYONE, label: 'Anyone with the Link' },
];

const ACCESS_ROLE_OPTIONS = [
  { value: SHARE_LINK_ACCESS_ROLES.EDITOR, label: 'Editor' },
  { value: SHARE_LINK_ACCESS_ROLES.VIEWER, label: 'Viewer' },
];

export function ShareLinkAccessSection({ shareLink, onUpdate }: Props) {
  const { t } = useTranslation();

  const handleAccessTypeChange = useCallback(
    async (value: string) => {
      // Reset role to Viewer when changing access type
      await onUpdate(shareLink._id, {
        accessType: value,
        accessRole: SHARE_LINK_ACCESS_ROLES.VIEWER,
      });
    },
    [shareLink._id, onUpdate],
  );

  const handleAccessRoleChange = useCallback(
    async (value: string) => {
      await onUpdate(shareLink._id, { accessRole: value });
    },
    [shareLink._id, onUpdate],
  );

  const showRoleSelect = shareLink.access?.type !== SHARE_LINK_ACCESS_TYPES.RESTRICTED;

  return (
    <div className="space-y-3">
      <div className={showRoleSelect ? 'flex gap-2' : ''}>
        <div className={showRoleSelect ? 'flex-1 space-y-2' : 'space-y-2'}>
          <Label>{t('General Access')}</Label>
          <Select
            value={shareLink.access?.type ?? SHARE_LINK_ACCESS_TYPES.RESTRICTED}
            onValueChange={handleAccessTypeChange}
          >
            <SelectTrigger className="cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACCESS_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showRoleSelect && (
          <div className="w-28 shrink-0 self-end">
            <Select
              value={shareLink.access?.role ?? SHARE_LINK_ACCESS_ROLES.VIEWER}
              onValueChange={handleAccessRoleChange}
            >
              <SelectTrigger className="w-28 cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
